import { useTooltipZIndex } from "@freightsimple/ui-core/Theme/overlayZIndex";
import { CrownIcon } from "@phosphor-icons/react/Crown";
import * as TooltipPrimitive from "@radix-ui/react-tooltip";
import type { CSSProperties } from "react";

// A bespoke, premium tooltip that the opinionated ui-core Tooltip deliberately
// can't express (custom gradient surface, white text, matched arrow). It lives
// as a feature-scoped CustomComponent rather than in ui-core, and drives its
// hover behaviour with the Radix Tooltip primitive directly.

const TOOLTIP_COPY =
  "This is an exclusive rate negotiated by FreightSimple on behalf of your company. Only your company has access to this pricing.";

// Bottom of the navy→black gradient — also the arrow fill so the arrow reads as
// a continuation of the tooltip body.
const TOOLTIP_DARK_BOTTOM = "#0a0e26";

// Layered gradients: a warm/gold tint near the top fading out, over the
// navy → near-black base. Kept as a style object (not Tailwind) because there
// is no compact utility for a dual-gradient stack; referenced via `style={...}`
// (a variable, not an inline object literal, so no-inline-styles is satisfied).
const tooltipSurfaceStyle: CSSProperties = {
  background: `linear-gradient(180deg, rgba(250, 204, 11, 0.15) 0%, rgba(250, 204, 11, 0) 45%), linear-gradient(180deg, #1f2a5e 0%, ${TOOLTIP_DARK_BOTTOM} 100%)`,
  borderRadius: "8px",
  padding: "18px 22px",
  width: "300px",
  boxShadow: "0 12px 32px rgba(0, 0, 0, 0.25)",
};

// Diagonal indigo gradient for the pill itself.
const badgePillStyle: CSSProperties = {
  background:
    "linear-gradient(135deg, #8f9df2 -30%, #4658e0 50%, #394cde 130%)",
};

// Arrow fill matches the bottom of the tooltip gradient.
const tooltipArrowStyle: CSSProperties = { fill: TOOLTIP_DARK_BOTTOM };

function QuotesExclusiveRateBadgeCustomComponent() {
  const tooltipZ = useTooltipZIndex();
  return (
    <TooltipPrimitive.Provider delayDuration={100}>
      <TooltipPrimitive.Root>
        <TooltipPrimitive.Trigger asChild>
          <span
            className="inline-flex cursor-help items-center gap-1.5 rounded-full px-2.5 py-1 text-xs leading-none font-medium whitespace-nowrap text-white shadow-sm"
            style={badgePillStyle}
          >
            <CrownIcon size={14} weight="fill" color="#facc0b" />
            Exclusive Rate
          </span>
        </TooltipPrimitive.Trigger>
        <TooltipPrimitive.Portal>
          <TooltipPrimitive.Content
            side="top"
            sideOffset={6}
            style={{ ...tooltipSurfaceStyle, zIndex: tooltipZ }}
          >
            <div className="text-[15px] leading-relaxed text-white">
              {TOOLTIP_COPY}
            </div>
            <TooltipPrimitive.Arrow
              width={14}
              height={7}
              style={tooltipArrowStyle}
            />
          </TooltipPrimitive.Content>
        </TooltipPrimitive.Portal>
      </TooltipPrimitive.Root>
    </TooltipPrimitive.Provider>
  );
}

export default QuotesExclusiveRateBadgeCustomComponent;
